// Core packages
import { formatDate } from "@angular/common";
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";

// Third party packages
import { MatDialog } from "@angular/material";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import moment from "moment";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { combineLatest, merge, Subscription } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged, finalize, startWith, take,
  tap
} from "rxjs/operators";

// Custom packages
import { PlatformNotesService } from "src/app/components/platform-note-card/platformNotes.service";
import { AuthService } from "src/app/modules/auth/auth.service";
import { BalancesService } from "src/app/modules/balances/balances.service";
import { AddInvoiceDialogComponent } from "src/app/modules/balances/components/add-invoice-dialog/add-invoice-dialog.component";
import { EditInvoiceDialogComponent } from "src/app/modules/balances/components/edit-invoice-dialog/edit-invoice-dialog.component";
import { BalanceEditNoteDialogComponent } from 'src/app/modules/balances/components/edit-note-dialog/edit-note-dialog.component';
import { BalancePrintDetailDialogComponent } from 'src/app/modules/balances/components/print-detail-dialog/print-detail-dialog.component';
import { BalanceViewDetailDialogComponent } from 'src/app/modules/balances/components/view-detail-dialog/view-detail-dialog.component';
import { InvoicesService } from "src/app/modules/balances/invoices.service";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { ViewNotesDialogComponent } from "src/app/shared/components/dialogs/document-note-dialog/view-notes-dialog.component";
import { Balance } from "src/app/shared/models/balance/balance.model";
import { IUser } from "src/app/shared/models/user/user.interface";
import { DateTimeFormatPipe } from "src/app/shared/pipes/date-format.pipe";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ListsService } from "src/app/shared/services/lists.service";
import { ThemeService } from "src/app/shared/services/theme.service";
import { InvoicesDataSource } from "src/app/shared/utils/data-sources/invoices-data-source";

interface Content {
  text?: string;
  style: string;
  margin?: number[];
  table?: any;
  layout?: any;
};

@Component({
  selector: "app-balance-detail",
  templateUrl: "./balance-detail.page.html",
  styleUrls: ["./balance-detail.page.scss"],
})
export class BalanceDetailComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  balance: Balance = new Balance();

  detailCardIsLoading = true;
  deleteCardIsLoading = true;

  // BEGIN - Table balances
  displayedColumns: string[] = ['index', "number", "date", "file", "actions"];
  dataSource: InvoicesDataSource;
  @ViewChild("sort1", { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  // @ViewChild('input', { static: true }) input: ElementRef;
  numberFilter: FormControl = new FormControl("");
  dateFilter: FormControl = new FormControl("");
  fileFilter: FormControl = new FormControl("");
  // END - Table balances

  // BEGIN - Add balance form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  notesField: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  // END - Add balance form
  deleteBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.delete"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "warn",
    spinnerColor: "warn",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-trash",
      inline: true,
    },
  };
  dateTimeFormat: DateTimeFormatPipe;

  displayedTotal = 0;
  previousValue: any = {};
  availableOptions: any = [];
  subscription: any;
  notes: any[] = [];
  companyId: string;
  companyName = "";
  companyTaxCode = "";
  companyVatNumber = "";
  ownerId: string;
  ownerName = "";
  ownerTaxCode = "";
  ownerVatNumber = "";
  propertyName = "";
  propertyTaxCode = "";
  invoiceHolder = "property";
  administrationEndDate = "";
  administrationStartDate = "";

  isThisFirstBalance = false;
  originalBalanceDetail = [];

  showInvoice = true;
  balanceNumber = 0;
  balanceDetailsView = '';

  propertyId = '';

  /**
   * Class constructor
   */
  constructor(
    private configService: ConfigService,
    private activatedRoute: ActivatedRoute,
    private balancesService: BalancesService,
    private propertiesService: PropertiesService,
    private router: Router,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private authService: AuthService,
    public dialog: MatDialog,
    private cookieService: CookieService,
    private companiesService: CompaniesService,
    private invoicesService: InvoicesService,
    private listsService: ListsService,
    private platformNotesService: PlatformNotesService,
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("balances.detail.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Set pdf fonts
    pdfMake.vfs = pdfFonts.pdfMake.vfs;

    // Get requested balance data
    const balanceId = this.activatedRoute.snapshot.params.balanceId;
    this.subscriptions.push(
      this.balancesService.get(balanceId).subscribe(
        (res) => {
          if (res.status) {
            this.propertyId = res.data.property._id;

            this.loadBalanceData(res.data);
            this.balanceNumber = res.data.number;
            this.balanceDetailsView = 
              res.data.status !== 'open' && 
              res.data.detailCopy && 
              Array.isArray(res.data.detailCopy) && 
              res.data.detailCopy.length;

            if (res.data.status && res.data.status === "closed") {
              // Load first set of data
              this.loadInvoicesPage();

              // Reset pagination after sorting
              this.subscriptions.push(
                this.sort.sortChange.subscribe(
                  () => (this.paginator.pageIndex = 0)
                )
              );

              // Once user change sorting OR change page
              // let's reload the page data
              this.subscriptions.push(
                merge(this.sort.sortChange, this.paginator.page)
                  .pipe(tap(() => this.loadInvoicesPage()))
                  .subscribe()
              );

              // Handle server-side filtering through column-specific search
              // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
              this.subscriptions.push(
                combineLatest([
                  this.numberFilter.valueChanges.pipe(
                    startWith(null as string)
                  ),
                  this.dateFilter.valueChanges.pipe(startWith(null as string)),
                  this.fileFilter.valueChanges.pipe(startWith(null as string)),
                ])
                  .pipe(
                    debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
                    distinctUntilChanged() // Eliminate duplicate values
                  )
                  .subscribe((res) => {
                    // Load page only if at least one value is not null
                    if (!res.every((value) => value === null)) {
                      this.loadInvoicesPage();
                    }
                  })
              );
            } else {
              this.showInvoice = false;
            }
            return;
          }
          // Something bad happened
          // redirect user to balances list
          this.router.navigate([this.lang, "app", "balances", "list"]);
        },
        (err) => {
          // if we came here users with given userId do not exist
          this.helperService.handleError(err);
          this.router.navigate([this.lang, "errors", "404"]);
        }
      )
    );

    // Init data source
    this.dataSource = new InvoicesDataSource(
      this.invoicesService,
      this.helperService
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all balances
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Handle params change
   *
   * @since 1.0.0
   */
  loadBalanceData(balance: any): void {
    // Set permission data
    this.balance.setData(balance);
    let currentNumber = 0;
    if (balance && balance.number) {
      currentNumber = balance.number;
    }

    const propertyId =
      balance.property && balance.property._id
        ? balance.property._id
        : undefined;
    if (propertyId) {
      this.subscriptions.push(
        this.balancesService
          .getList(0, 10, "createdAt", "asc", { propertyId: propertyId })
          .subscribe(
            (res) => {
              if (res.status) {
                if (
                  res.data.items &&
                  Array.isArray(res.data.items) &&
                  res.data.items.length > 0
                ) {
                  const index = res.data.items.findIndex((el: any) => {
                    return el.number === currentNumber;
                  });
                  if (index === 0) {
                    this.isThisFirstBalance = true;
                  }
                  // Init edit form
                  this.initForm();
                }
                return;
              }
            },
            (err) => {
              // if we came here users with given userId do not exist
              // this.helperService.handleError(err);
            }
          )
      );
    }

    // Set property id
    if (balance.status === "open") {
      this.subscription =
        balance.property && balance.property.subscription
          ? balance.property.subscription
          : undefined;
    } else {
      this.subscription = balance.subscription;
    }
    // Set companyId
    this.companyId =
      balance.property &&
      balance.property.admin &&
      balance.property.admin.company
        ? balance.property.admin.company
        : undefined;
    // Set company ownerId
    this.ownerId =
      balance.property && balance.property.owner
        ? balance.property.owner
        : undefined;

    this.propertyName =
      balance.property && balance.property.name ? balance.property.name : "";
    this.propertyTaxCode =
      balance.property && balance.property.taxCode
        ? balance.property.taxCode
        : "";

    this.invoiceHolder =
      balance.property && balance.subscription.invoiceHolder
        ? balance.subscription.invoiceHolder
        : "property";
    if (this.invoiceHolder === "property" || this.invoiceHolder === "admin") {
      this.subscriptions.push(
        this.companiesService.get(this.companyId).subscribe(
          (res) => {
            if (res && res.status && res.data) {
              this.companyName = res.data.name;
              this.companyTaxCode = res.data.taxCode
                ? res.data.taxCode
                : "";
              this.companyVatNumber = res.data.vatNumber
                ? res.data.vatNumber
                : "";
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        )
      );
    } else if (this.invoiceHolder === "owner") {
      this.subscriptions.push(
        this.companiesService.get(this.ownerId).subscribe(
          (res) => {
            if (res && res.status && res.data) {
              this.ownerName = res.data.name;
              this.ownerTaxCode = res.data.taxCode
                ? res.data.taxCode
                : "";
              this.ownerVatNumber = res.data.vatNumber
                ? res.data.vatNumber
                : "";
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        )
      );
    }

    if (
      balance.property.administrationEndDate &&
      moment(balance.property.administrationEndDate).isValid()
    ) {
      this.administrationEndDate = moment(balance.property.administrationEndDate).format("DD/MM");
    }
    if (
      balance.property.administrationStartDate &&
      moment(balance.property.administrationStartDate).isValid()
    ) {
      this.administrationStartDate = moment(balance.property.administrationStartDate).format("DD/MM");
    }

    // Set notes
    this.notes = [];
    if (balance.notes && Array.isArray(balance.notes)) {
      for (const item of balance.notes) {
        this.notes.push({
          _id: item._id,
          property: false,
          category: item.category ? item.category : '',
          createdBy: item.createdBy,
          createdAt: item.createdAt,
          title: item.title,
          text: item.text,
          file: item.file
        });
      }
    }
    
    if (balance.status !== 'closed') {
      this.subscriptions.push(
        this.platformNotesService.getProperty(this.propertyId, 0, 100)
        .subscribe((res) => {
          if (res.data.items && Array.isArray(res.data.items)) {
            for (const item of res.data.items) {
              if (
                item.category === 'subscription' ||
                item.category === 'work-account'
              ) {
                this.notes.push({
                  property: true,
                  category: item.category,
                  createdBy: item.createdBy,
                  createdAt: item.createdAt,
                  title: item.title,
                  text: item.description,
                  file: item.file
                });
              }
            }
          }
        })
      );
    }

    // Stop loading in delete form
    this.deleteCardIsLoading = false;

    // // Init edit form
    // this.initForm();

    if (this.subscription && this.subscription.key) {
      // Get available code options
      this.getAvailableOptions(this.subscription.key);
    }

    // Init total value
    this.initTotal();

    // Make detail form card visible by disabling his card-spinner
    this.detailCardIsLoading = false;
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    const balanceData = this.balance.getData();
    if (balanceData.detail && Array.isArray(balanceData.detail)) {
      this.originalBalanceDetail = balanceData.detail;
    }

    // Detail transformation (grouping for open accounts, consolidation of
    // scan/insert families for pending/closed accounts, special descriptions and
    // amounts) is now performed authoritatively by the backend on the status
    // transition (PATCH /balances/{id}). The FE renders the detail/detailCopy/total
    // returned by the server as-is and no longer transforms them client-side.
    this.model = balanceData;

    this.fields = [];
    this.notesField = [];

    // Set fields
    this.fields = [
      {
        className: "section-label mb-0",
        template:
          "<hr /><div><strong>" +
          this.translateService.instant("balances.fields.detail.label") +
          "</strong></div><br />",
      },
      {
        key: "detail",
        type: "repeat",
        fieldArray: {
          fieldGroupClassName: "row",
          fieldGroup: [
            {
              className: "col-12 col-md-4 col-lg-2",
              type: "input",
              key: "code",
              id: "111",
              templateOptions: {
                label: this.translateService.instant(
                  "balances.fields.detail.code"
                ),
                placeholder: this.translateService.instant(
                  "balances.fields.detail.code"
                ),
                disabled: true,
              },
            },
            {
              className: "col-12 col-md-4 col-lg-4",
              type: "textarea",
              key: "description",
              templateOptions: {
                label: this.translateService.instant(
                  "balances.fields.detail.description"
                ),
                placeholder: this.translateService.instant(
                  "balances.fields.detail.description"
                ),
              },
              expressionProperties: {
                "templateOptions.disabled": () => {
                  return this.model.status && this.model.status === "closed";
                },
                "templateOptions.rows": (model) => {
                  return model.description.length
                    ? Math.ceil(model.description.length / 50)
                    : 1; // 50 is the average char number in one line
                },
              },
            },
            {
              className: "col-12 col-md-4 col-lg-1",
              type: "input",
              key: "quantity",
              templateOptions: {
                type: "number",
                label: this.translateService.instant(
                  "balances.fields.detail.quantity"
                ),
                placeholder: this.translateService.instant(
                  "balances.fields.detail.quantity"
                ),
                min: 0,
                required: true,
              },
              expressionProperties: {
                "templateOptions.readonly": () => {
                  return this.model.status && this.model.status === "closed";
                },
              },
            },
            {
              className: "col-12 col-md-4 col-lg-2",
              type: "input",
              key: "unitPrice",
              templateOptions: {
                type: "number",
                label: this.translateService.instant(
                  "balances.fields.detail.unitPrice"
                ),
                placeholder: this.translateService.instant(
                  "balances.fields.detail.unitPrice"
                ),
                min: 0,
                required: true,
              },
              expressionProperties: {
                "templateOptions.readonly": () => {
                  return this.model.status && this.model.status === "closed";
                },
              },
            },
            {
              className: "col-12 col-md-4 col-lg-2",
              type: "input",
              key: "totalRow",
              templateOptions: {
                type: "number",
                label: this.translateService.instant(
                  "balances.fields.detail.totalRow"
                ),
                placeholder: this.translateService.instant(
                  "balances.fields.detail.totalRow"
                ),
                readonly: true,
                min: 0,
              },
            },
          ],
        },
        templateOptions: {
          askConfirm: true,
          status: this.model.status,
          addWithDialog: true,
          delBnt: true,
        },
        expressionProperties: {
          "templateOptions.options": () => {
            return this.availableOptions;
          },
        },
      },
      {
        className: "section-label",
        template: "<hr /><br />",
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-9",
          },
          {
            className: "col-2",
            type: "input",
            key: "discount",
            templateOptions: {
              label: this.translateService.instant("balances.fields.discount"),
              placeholder: this.translateService.instant(
                "balances.fields.discount"
              ),
              required: true,
              type: "number",
              min: 0,
              max: 1000,
            },
            expressionProperties: {
              "templateOptions.onChange": (model: any) => {
                this.updateTotal(model);
              },
              "templateOptions.readonly": () => {
                return this.model.status && this.model.status === "closed";
              },
            },
          },
        ],
      },
    ];

    // Set notes field
    this.notesField = [
      {
        className: "section-label mb-0 mt-4",
        template:
          "<hr /><div><strong>" +
          this.translateService.instant("balances.fields.newNote") +
          "</strong></div>",
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            type: "textarea",
            key: "notes.text",
            templateOptions: {
              rows: 5,
              maxLength: 5000,
            },
            expressionProperties: {
              "templateOptions.description":
                '(model[field.key]||"").length+"/" +field.templateOptions.maxLength',
            },
          },
        ],
      },
    ];
  }

  /**
   * Get available balance detail options
   *
   * @since 1.0.0
   */
  getAvailableOptions(code: string) {
    this.availableOptions = this.configService.availableAccessoriesCodes.filter(
      (element) => {
        return element.keys.includes(code);
      }
    );
  }

  /**
   * Handle balance status change
   *
   * @since 1.0.0
   */
  onStatusChange() {
    const status = this.model.status;
    const property = this.model.property;
    if (!status || !property) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant("balances.status.changeMessage"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          // User hit decline button
          return;
        }

        const propertyNotes = this.notes.filter((el: any) => el.property);

        // The backend now recomputes detail/detailCopy/total authoritatively on
        // the transition. The FE sends only the transition intent plus the data
        // the backend still needs: the starting status (must match the persisted
        // one for idempotency), the property (used to create the next open
        // balance) and the subscription snapshot. propertyNotes are merged by the
        // backend on pending->closed. detail/detailCopy/total are NOT sent.
        const balanceId = this.balance.getId();
        this.subscriptions.push(
          this.balancesService
            .changeStatus(balanceId, {
              status,
              property,
              subscription: this.subscription,
              propertyNotes
            })
            .subscribe(
              (res2) => {
                if (res2.status) {
                  this.toastrService.success(res2.message);
                  // Reload from the backend to reflect the recomputed
                  // detail/detailCopy/total/status (and the populated property).
                  this.subscriptions.push(
                    this.balancesService.get(balanceId).subscribe(
                      (res) => {
                        if (res.status) {
                          this.loadBalanceData(res.data);
                        }
                      },
                      (err) => {
                        this.helperService.handleError(err);
                      }
                    )
                  );
                }
              },
              (err) => {
                // Business errors (422 missing billing codes, 409 stale status,
                // 400 invalid transition) carry an explicit backend message.
                // Show it directly: the generic handleError would mis-render the
                // 422 payload (its `data` is not in the validation-error shape).
                if (err && err.error && err.error.message) {
                  this.toastrService.error(err.error.message);
                } else {
                  this.helperService.handleError(err);
                }
              }
            )
        );
      });
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    if (this.displayedTotal !== this.model.total) {
      this.model.total = this.displayedTotal;
    }
    if (typeof this.model.total === "undefined" || !this.model.total) {
      this.model.total = 0;
    }
    if (typeof this.model.detail === "undefined" || !this.model.detail) {
      this.model.detail = [];
    }
    const balanceData = this.balance.getData();
    if (balanceData.status === "closed") {
      this.model.detail = this.originalBalanceDetail;
    }

    // Handle balance notes
    let notes: any[] = [];
    if (this.model.notes.text) {
      if (this.balance.get('notes') && Array.isArray(this.balance.get('notes'))) {
        notes = JSON.parse(JSON.stringify(this.balance.get('notes')));
      }
      notes.push({
        text: this.model.notes.text,
        createdAt: moment().toISOString(),
        createdBy: this.loggedUser._id,
      });
      this.model.notes = notes;
    }

    this.subscriptions.push(
      this.balancesService
        .update(this.balance.getId(), this.model)
        .pipe(finalize(() => (this.formBtnOptions.active = false)))
        .subscribe(
          (res) => {
            if (res.status) {
              const previousProperty = this.balance.get("property");
              this.balance.setData(res.data);
              this.balance.set("property", previousProperty);

              this.toastrService.success(res.message);

              // Get requested balance data
              const balanceId = this.activatedRoute.snapshot.params.balanceId;
              this.subscriptions.push(
                this.balancesService.get(balanceId).subscribe(
                  (res) => {
                    if (res.status) {
                      this.loadBalanceData(res.data);
                      return;
                    }
                  },
                  (err) => {
                    // if we came here users with given userId do not exist
                    this.helperService.handleError(err);
                  }
                )
              );
            }
          },
          (err) => {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }

  /**
   * Returns calculated total value
   *
   * @since 1.0.0
   */
  calculateTotal(): number {
    let total = 0;
    if (this.model.detail) {
      this.model.detail.forEach((element: any, index: number) => {
        const detailControl = this.form.controls.detail as FormArray;
        const rowControl = detailControl
          ? (detailControl.controls[index] as FormGroup)
          : undefined;
        const totalRowControl = rowControl
          ? rowControl.controls["totalRow"]
          : undefined;
        const totalRow = element.quantity * element.unitPrice;
        if (totalRow !== element.totalRow) {
          element.totalRow = totalRow;
          if (totalRowControl) {
            totalRowControl.setValue(totalRow);
          }
        }
        total += totalRow;
      });

      return total;
    }
  }

  /**
   * Returns a new price with given discount applied
   *
   * @since 1.0.0
   */
  applyDiscount(price: number, discount: number): number {
    if (!price) {
      return;
    }
    if (!discount || discount <= 0) {
      return price;
    }
    return price - price * (discount / 100);
  }

  /**
   * Initialize total value
   *
   * @since 1.0.0
   */
  initTotal() {
    const total = this.calculateTotal();
    this.displayedTotal = this.applyDiscount(total, this.model.discount);
    if (this.displayedTotal !== this.model.total) {
      console.warn("totals are different on init", this.displayedTotal, total);
    }
  }

  /**
   * Watch for value changes and update total value
   *
   * @since 1.0.0
   */
  updateTotal(value: any) {
    if (value !== this.previousValue) {
      this.previousValue = value;
      const detailControl = this.form.controls.detail;
      const discountControl = this.form.controls.discount;
      let currentDiscount = discountControl.value;
      let total = 0;

      if (detailControl) {
        detailControl.valueChanges.subscribe((res) => {
          total = this.calculateTotal();
          this.displayedTotal = this.applyDiscount(total, currentDiscount);
        });
      }

      if (discountControl) {
        discountControl.valueChanges.subscribe((res) => {
          currentDiscount = res;
          this.displayedTotal = this.applyDiscount(total, currentDiscount);
        });
      }
    }
  }

  /**
   * Generate pdf with formatted balance data
   *
   * @since 1.0.0
   */
  onGeneratePdf() {
    // Set balance notes
    const notes: any[] = [];
    if (this.notes) {
      this.notes.forEach((note) => {
        // Date header
        notes.push({
          text: moment(note.createdAt).format("DD/MM/YYYY hh:mm"),
          style: "text",
          margin: [0, 0, 0, 0],
        });
        // Text body
        notes.push({
          text: note.text,
          style: "text",
          margin: [0, 0, 0, 10],
        });
      });
    }

    // Set pdf style macros
    const colWidths = ["*", 150, "*", "*", "*"];
    const fontSize = 10;
    const headerFontSize = fontSize + 5;
    const subHeaderFontSize = fontSize + 1;
    const tableHeaderFontSize = fontSize + 1;
    const tableColor = "#d8e4ec";
    const headingColor = "#a9d4f1";

    let balanceDetail = [];
    balanceDetail.push([
      "Codice",
      "Descrizione",
      "Quantità",
      "Prezzo unitario",
      "Totale riga",
    ]);
    if (this.balance.get("detail") && this.balance.get("detail").length) {
      let totScanIns = 0;
      let totInsFollowing = 0;
      let totScanFollowing = 0;
      let scanIns = false;
      this.balance.get("detail").forEach((el: any) => {
        if (
          el.code == "MOD-DOC-DIG-A4" ||
          el.code == "FILE-PDF" ||
          el.code == "SC-PP" ||
          el.code == "SC" ||
          el.code == "MOD-DOC-CAR-A4" ||
          el.code == "MOD-DOC-CAR-A3" ||
          el.code == "MOD-DOC-CAR-A2" ||
          el.code == "MOD-DOC-CAR-A1" ||
          el.code == "MOD-DOC-CAR-A0" ||
          el.code == "SC-FF"
        ) {
          totScanIns = totScanIns + el.totalRow;
          scanIns = true;
          if (el.code == "MOD-DOC-DIG-A4" || el.code == "FILE-PDF") {
            totInsFollowing = totInsFollowing + el.totalRow;
          }
          if (
            el.code == "SC-PP" ||
            el.code == "SC" ||
            el.code == "MOD-DOC-CAR-A4" ||
            el.code == "MOD-DOC-CAR-A3" ||
            el.code == "MOD-DOC-CAR-A2" ||
            el.code == "MOD-DOC-CAR-A1" ||
            el.code == "MOD-DOC-CAR-A0" ||
            el.code == "SC-FF"
          ) {
            totScanFollowing = totScanFollowing + el.totalRow;
          }
        }
        if (el.code === "UNIPACC001") {
          el.description = this.translateService.instant(
            "companies.fields.subscription.housingUnits.title"
          );
          el.quantity = 1;
          el.unitPrice = el.totalRow;
        }
        if (el.code === "BOXPACC001") {
          el.description = this.translateService.instant(
            "companies.fields.subscription.boxUnits.title"
          );
          el.quantity = 1;
          el.unitPrice = el.totalRow;
        }
      });
      // balanceDetail.push([
      //   "Codice",
      //   "Descrizione",
      //   "Quantità",
      //   "Prezzo unitario",
      //   "Totale riga",
      // ]);

      const balanceDetailRows = this.balance
        .get("detail")
        .filter(
          (el) =>
            el.code !== "MOD-DOC-DIG-A4" &&
            el.code !== "FILE-PDF" &&
            el.code !== "SC-PP" &&
            el.code !== "SC" &&
            el.code !== "MOD-DOC-CAR-A4" &&
            el.code !== "MOD-DOC-CAR-A3" &&
            el.code !== "MOD-DOC-CAR-A2" &&
            el.code !== "MOD-DOC-CAR-A1" &&
            el.code !== "MOD-DOC-CAR-A0" &&
            el.code !== "SC-FF"
        )
        .map((el) => {
          return [
            `${el.code}`,
            `${el.description}`,
            {
              text: `${el.quantity}`,
              alignment: "right",
            },
            {
              text: `€ ${el.unitPrice.toFixed(2)}`,
              alignment: "right",
            },
            {
              text: `€ ${el.totalRow.toFixed(2)}`,
              alignment: "right",
            },
          ];
        });
      balanceDetail = balanceDetail.concat(balanceDetailRows);

      if (scanIns) {
        if (
          this.subscription &&
          (this.subscription.scanInsFirstCode ||
            this.subscription.scanInsNextCode)
        ) {
          let code = "";
          let description = "";
          if (this.isThisFirstBalance) {
            code = this.subscription.scanInsFirstCode;
            const descriptionCode =
              this.configService.availableScanCodes.filter((element) => {
                return element.value === code;
              });
            if (
              descriptionCode &&
              Array.isArray(descriptionCode) &&
              descriptionCode.length
            ) {
              description = descriptionCode[0].description;
              description = description.replace(/\s\s+/g, " ");
              description = description.replace(
                "#startDate#",
                moment(this.subscription.startDate).format("DD-MM-YYYY")
              );
              description = description.replace(
                "#endDate#",
                moment(this.subscription.endDate).format("DD-MM-YYYY")
              );
            }
            const rowScanIns = [
              `${code}`,
              `${description}`,
              {
                text: `1`,
                alignment: "right",
              },
              {
                text: `€ ${totScanIns.toFixed(2)}`,
                alignment: "right",
              },
              {
                text: `€ ${totScanIns.toFixed(2)}`,
                alignment: "right",
              },
            ];

            balanceDetail.push(rowScanIns);
          } else {
            // 4 - INSERIMENTI CARTACEI E DIGITALI SUCCESSIVI
            if (this.subscription.scanInsNext) {
              //  INSSUCC001
              code = this.subscription.scanInsNextCode;
              const descriptionCode =
                this.configService.availableInsCodesNext.filter((element) => {
                  return element.value === code;
                });
              if (
                descriptionCode &&
                Array.isArray(descriptionCode) &&
                descriptionCode.length
              ) {
                description = descriptionCode[0].description;
                description = description.replace(/\s\s+/g, " ");
              }
              if (totInsFollowing > 0) {
                const rowScanIns = [
                  `${code}`,
                  `${description}`,
                  {
                    text: `1`,
                    alignment: "right",
                  },
                  {
                    text: `€ ${totInsFollowing.toFixed(2)}`,
                    alignment: "right",
                  },
                  {
                    text: `€ ${totInsFollowing.toFixed(2)}`,
                    alignment: "right",
                  },
                ];

                balanceDetail.push(rowScanIns);
              }
            } else if (this.subscription.flatRate) {
              //  FINSSUCC001
              code = this.subscription.flatRateCode;
              const descriptionCode =
                this.configService.availableInsCodesNextForfait.filter(
                  (element) => {
                    return element.value === code;
                  }
                );
              if (
                descriptionCode &&
                Array.isArray(descriptionCode) &&
                descriptionCode.length
              ) {
                description = descriptionCode[0].description;
                description = description.replace(/\s\s+/g, " ");
              }
              if (totInsFollowing > 0) {
                const rowScanIns = [
                  `${code}`,
                  `${description}`,
                  {
                    text: `1`,
                    alignment: "right",
                  },
                  {
                    text: `€ ${this.subscription.flatRateFee.toFixed(2)}`,
                    alignment: "right",
                  },
                  {
                    text: `€ ${this.subscription.flatRateFee.toFixed(2)}`,
                    alignment: "right",
                  },
                ];

                balanceDetail.push(rowScanIns);
              }
            }

            // 5 - SCANSIONI SUCCESSIVE
            if (this.subscription.scanNext) {
              //  SCASUCC001
              code = this.subscription.scanNextCode;
              const descriptionCode =
                this.configService.availableScanCodesNext.filter((element) => {
                  return element.value === code;
                });
              if (
                descriptionCode &&
                Array.isArray(descriptionCode) &&
                descriptionCode.length
              ) {
                description = descriptionCode[0].description;
                description = description.replace(/\s\s+/g, " ");
              }
              if (totScanFollowing > 0) {
                const rowScanIns = [
                  `${code}`,
                  `${description}`,
                  {
                    text: `1`,
                    alignment: "right",
                  },
                  {
                    text: `€ ${totScanFollowing.toFixed(2)}`,
                    alignment: "right",
                  },
                  {
                    text: `€ ${totScanFollowing.toFixed(2)}`,
                    alignment: "right",
                  },
                ];

                balanceDetail.push(rowScanIns);
              }
            } else if (this.subscription.flatRateScan) {
              //  FSCASUCC001
              code = this.subscription.flatRateScanCode;
              const descriptionCode =
                this.configService.availableScanCodesNextForfait.filter(
                  (element) => {
                    return element.value === code;
                  }
                );
              if (
                descriptionCode &&
                Array.isArray(descriptionCode) &&
                descriptionCode.length
              ) {
                description = descriptionCode[0].description;
                description = description.replace(/\s\s+/g, " ");
              }
              if (totScanFollowing > 0) {
                const rowScanIns = [
                  `${code}`,
                  `${description}`,
                  {
                    text: `1`,
                    alignment: "right",
                  },
                  {
                    text: `€ ${this.subscription.flatRateScanFee.toFixed(2)}`,
                    alignment: "right",
                  },
                  {
                    text: `€ ${this.subscription.flatRateScanFee.toFixed(2)}`,
                    alignment: "right",
                  },
                ];

                balanceDetail.push(rowScanIns);
              }
            }
          }
        }
      }
    }

    const content: Content[] = [];
    // Title
    content.push(  {
      text: `Riepilogo conto n. ${this.balance.get("number")}`,
      style: "header",
      margin: [0, 20, 0, 20],
    });
    // Balance
    content.push(  {
      text: `Numero conto: ${this.balance.get("number")}`,
      style: "subHeader",
      margin: [0, 0, 0, 8],
    });

    if (this.invoiceHolder === "property") {
      content.push({
        text: `Intestatario fattura: ${this.propertyName}`,
        style: "subHeader",
        margin: [0, 0, 0, 8],
      });
      content.push({
        text: `Nome dell'immobile: ${this.propertyName}`,
        style: "subHeader",
        margin: [0, 0, 0, 8],
      });
      content.push({
        text: `Codice fiscale: ${this.propertyTaxCode}`,
        style: "subHeader",
        margin: [0, 0, 0, 8],
      });
      content.push({
        text: `Nome della società: ${this.companyName}`,
        style: "subHeader",
        margin: [0, 0, 0, 8],
      });
    }
    if (this.invoiceHolder === "owner") {
      content.push({
        text: `Intestatario fattura: ${this.ownerName}`,
        style: "subHeader",
        margin: [0, 0, 0, 8],
      });
      content.push({
        text: `Nome dell'immobile: ${this.propertyName}`,
        style: "subHeader",
        margin: [0, 0, 0, 8],
      });
      content.push({
        text:
          this.ownerTaxCode !== ""
            ? `Codice fiscale: ${this.ownerTaxCode}`
            : `P.IVA: ${this.ownerVatNumber}`,
        style: "subHeader",
        margin: [0, 0, 0, 8],
      });
      content.push({
        text: `Nome della società: ${this.ownerName}`,
        style: "subHeader",
        margin: [0, 0, 0, 8],
      });
    }
    if (this.invoiceHolder === "admin") {
      content.push({
        text: `Intestatario fattura: ${this.companyName}`,
        style: "subHeader",
        margin: [0, 0, 0, 8],
      });
      content.push({
        text: `Nome dell'immobile: ${this.propertyName}`,
        style: "subHeader",
        margin: [0, 0, 0, 8],
      });
      content.push({
        text:
          this.companyTaxCode !== ""
            ? `Codice fiscale: ${this.companyTaxCode}`
            : `P.IVA: ${this.companyVatNumber}`,
        style: "subHeader",
        margin: [0, 0, 0, 8],
      });
    }
    content.push({
      text: `Inizio esercizio contabile: ${this.administrationStartDate}`,
      style: "subHeader",
      margin: [0, 0, 0, 8],
    });
    content.push({
      text: `Fine esercizio contabile: ${this.administrationEndDate}`,
      style: "subHeader",
      margin: [0, 0, 0, 8],
    });

    content.push({
      style: "table",
      table: {
        widths: colWidths,
        body: balanceDetail,
      },
      layout: {
        fillColor: (rowIndex) => {
          return rowIndex % 2 === 0 ? tableColor : null;
        },
      },
    });
    content.push({
      style: "tableFooter",
      table: {
        widths: colWidths,
        body: [
          [
            "",
            "",
            "",
            "SCONTO",
            {
              text: `${this.balance.get("discount")}%`,
              alignment: "right",
            },
          ],
          [
            "",
            "",
            "",
            "TOTALE FATTURA",
            {
              text: `€ ${this.balance.get("total").toFixed(2)}`,
              alignment: "right",
            },
          ],
        ],
      },
      layout: {
        fillColor: headingColor,
      },
    });
    content.push({ text: "NOTE", style: "subHeader", margin: [0, 10, 0, 8] })

    const documentDefinition = {
      content: content.concat(notes),
      // [
      //   ...content,
      //   {
      //     style: "table",
      //     table: {
      //       widths: colWidths,
      //       body: balanceDetail,
      //     },
      //     layout: {
      //       fillColor: (rowIndex) => {
      //         return rowIndex % 2 === 0 ? tableColor : null;
      //       },
      //     },
      //   },
      //   {
      //     style: "tableFooter",
      //     table: {
      //       widths: colWidths,
      //       body: [
      //         [
      //           "",
      //           "",
      //           "",
      //           "SCONTO",
      //           {
      //             text: `${this.balance.get("discount")}%`,
      //             alignment: "right",
      //           },
      //         ],
      //         [
      //           "",
      //           "",
      //           "",
      //           "TOTALE FATTURA",
      //           {
      //             text: `€ ${this.balance.get("total").toFixed(2)}`,
      //             alignment: "right",
      //           },
      //         ],
      //       ],
      //     },
      //     layout: {
      //       fillColor: headingColor,
      //     },
      //   },
      //   // Notes
      //   { text: "NOTE", style: "subHeader", margin: [0, 10, 0, 8] },
      //   notes,
      // ],
      styles: {
        text: {
          fontSize,
        },
        header: {
          fontSize: headerFontSize,
          bold: true,
          margin: [0, 0, 0, 0],
          alignment: "center",
        },
        subHeader: {
          fontSize: subHeaderFontSize,
          bold: false,
          margin: [0, 10, 0, 5],
        },
        table: {
          fontSize,
          margin: [0, 0, 0, 0],
        },
        tableHeader: {
          bold: true,
          fontSize: tableHeaderFontSize,
          color: "black",
          margin: [0, 15, 0, 0],
          alignment: "center",
        },
        tableFooter: {
          bold: true,
          fontSize,
          color: "black",
          // color: '#dc3545',
          margin: [0, 0, 0, 15],
          // 			alignment: 'center'
        },
      },
    };

    pdfMake.createPdf(documentDefinition).open();
  }

  /**
   * Handle click to show document notes
   *
   * @since 1.0.0
   */
  onViewDocumentNotes(): void {
    const notes = this.notes.map((note) => {
      if (
        note.createdBy &&
        note.createdBy.firstName &&
        note.createdBy.lastName
      ) {
        note.createdBy = {
          ...note.createdBy,
          name: `${note.createdBy.firstName} ${note.createdBy.lastName}`,
        };
        delete note.createdBy.firstName;
        delete note.createdBy.lastName;
      }
    });

    this.dialog.open(ViewNotesDialogComponent, {
      data: {
        modalTitle: this.translateService.instant(
          "properties.documentsPage.noteList"
        ),
        notes: this.notes,
        balanceId: this.balance.getId(),
      },
      minWidth: "50%",
    });
  }

  /**
   * Remove current user from database
   *
   * @since 1.0.0
   */
  onDelete(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant("dialogs.confirm.message"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.subscriptions.push(
          this.balancesService
            .delete(this.balance.getId())
            .pipe(finalize(() => (this.deleteBtnOptions.active = false)))
            .subscribe(
              (res2) => {
                this.toastrService.success(res2.message);
                // Once the user is deleted, redirect to list
                this.router.navigate([this.lang, "app", "balances", "list"]);
                return;
              },
              (err) => {
                // Choose one of the following error handling
                // method. The first one shows a message right
                // under the form fields (if the form is properly
                // setted), the second one shows toastr
                // notifications for each error
                // this.helperService.handleFormError(this.passwordForm, err);
                this.helperService.handleError(err);
              }
            )
        );
      });
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadInvoicesPage(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search = {
      general: "", // this.input.nativeElement.value,
      number: this.numberFilter.value,
      date: this.dateFilter.value
        ? new Date(this.dateFilter.value).toISOString()
        : undefined,
      file: this.fileFilter.value,
      balanceNumber: this.balanceNumber,
    };
    this.dataSource.loadItems(
      start,
      length,
      this.sort.active,
      this.sort.direction,
      search
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetInvoicesFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting("invoices", 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = '';
    this.numberFilter.setValue(null);
    this.dateFilter.setValue(null);
    this.fileFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.sort.direction = "desc";
    this.sort.active = "number";
    this.sort._stateChanges.next();

    // Reset paginator
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;

    // Reload table's data
    this.loadInvoicesPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetInvoicesFilter(filter: FormControl): void {
    filter.reset();
    this.loadInvoicesPage();
  }

  /**
   * Export invoices in a file with given format
   *
   * @since 1.0.0
   */
  onInvoiceExport(): void {
    const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
    const fileName = `${this.translateService.instant(
      "invoices.exportFileName"
    )} ${date}`;
    this.subscriptions.push(
      this.invoicesService.getExport(this.balanceNumber).subscribe(
        (res) => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: "application/vnd.ms-excel",
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Add new invoice
   *
   * @since 1.0.0
   */
  onNewInvoice(): void {
    const dialogRef = this.dialog.open(AddInvoiceDialogComponent, {
      data: {
        balanceNumber: this.balance.get("number"),
      },
      width: "600px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        this.loadInvoicesPage();
        // if (!res) {
        //   return;
        // } else {
        //   this.loadInvoicesPage();
        // }
      });
  }

  /**
   * Edit invoice
   *
   * @since 1.0.0
   */
  onEditInvoice(invoice: any): void {
    const dialogRef = this.dialog.open(EditInvoiceDialogComponent, {
      data: {
        balanceNumber: this.balance.get("number"),
        invoiceId: invoice._id,
        number: invoice.number,
        date: invoice.date,
        file: invoice.file,
      },
      width: "600px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        this.loadInvoicesPage();
        // if (!res) {
        //   return;
        // } else {
        //   this.loadInvoicesPage();
        // }
      });
  }

  /**
   * Delete invoice
   *
   * @since 1.0.0
   */
  onDeleteInvoice(invoiceId: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.invoice.title"),
        message: this.translateService.instant("dialogs.invoice.message"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.close"),
      },
      width: "500px",
      disableClose: true,
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (res) {
          this.subscriptions.push(
            this.invoicesService.delete(invoiceId).subscribe(
              (res) => {
                if (res.status) {
                  this.loadInvoicesPage();
                }
              },
              (err) => {
                // if we came here users with given userId do not exist
                this.helperService.handleError(err);
              }
            )
          );
        }
      });
    return;
  }

  /**
   * Handle click to show balance details
   *
   * @since 1.0.0
   */
  onViewDetails(): void {
    this.dialog.open(BalanceViewDetailDialogComponent, {
      data: {
        modalTitle: this.translateService.instant(
          "balances.showDetail"
        ), 
        detail: this.balance.get("detailCopy"),
      },
      width: "80%",
    });
  }

  /**
   * Handle click to print balance details
   *
   * @since 1.0.0
   */
   onPrintDetails(): void {
    this.dialog.open(BalancePrintDetailDialogComponent, {
      data: {
        modalTitle: this.translateService.instant(
          "balances.printDetail"
        ), 
        detail: this.balance.get("detailCopy"),
      },
      width: "80%",
    });
  }
  
  /**
   * Handle edit
   *
   * @since 1.0.0
   */
  onEdit(
    text: string,
    noteId: string
  ): void {
    const editDialogRef = this.dialog.open(
      BalanceEditNoteDialogComponent,
      {
        minWidth: "40%",
        maxHeight: "98vh",
        data: {
          title: this.translateService.instant(
            "properties.structures.notes"
          ),
          text,
          balanceId: this.balance.getId(),
          noteId,
        },
        disableClose: true,
      }
    );
    editDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res || !(res.status === true || res.success === true)) {
          return;
        }
        // The updated text may be returned at the top level (`text`) or wrapped
        // under `data` depending on the API version; read it defensively.
        const updatedText =
          res.text !== undefined && res.text !== null
            ? res.text
            : res.data && res.data.text;
        const index = this.notes.findIndex((el: any) => {
          return (
            el._id.toString() === noteId.toString()
          );
        });
        if (index > -1 && updatedText !== undefined && updatedText !== null) {
          this.notes[index].text = updatedText;
        }
        return;
      });
  }

  /**
  * Handle delete
  *
  * @since 1.0.0
  */
  onDeleteNote(noteId: string): void {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      minWidth: "30%",
      maxHeight: "98vh",
      data: {
        title: this.translateService.instant("common.warnings.warning"),
        message: this.translateService.instant("common.warnings.noteDelete"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      disableClose: true,
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    confirmDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.subscriptions.push(
          this.balancesService
            .deleteNote(
              this.balance.getId(),
              noteId
            )
            .pipe(take(1))
            .subscribe(
              (res) => {
                if (res.status) {
                  this.toastrService.success(res.message);
                  const index = this.notes.findIndex((el: any) => {
                    return (
                      el._id.toString() === noteId.toString()
                    );
                  });
                  if (index > -1) {
                    this.notes.splice(index, 1);
                  }
                }
              },
              (err) => {
                // Choose one of the following error handling method.
                // 1) First one show a message right under the form fields
                //    (if the form is properly setted)
                // 2) Second one is used to show a mat-error under each field
                //    in the dynamic form (is meant to be used only in dynamicForm)
                // 3) Third one show toastr notifications for each error
                // 4) Fourth one is used to show mat-error under each field
                //    in the ngx-formly form (is mean to be used only with ngx-formly)
                // this.helperService.handleFormError(form, err);
                // this.helperService.handleDynamicFormError(form, err);
                this.helperService.handleError(err);
              }
            )
        );
        return;
      });
  }
}
